import React, { useEffect, useMemo, useState } from 'react';
import { GROUPS, classify } from '@/pages/DevelopersPage';
import { useBrand } from '@/lib/branding';

const METHOD_COLOR = {
  GET: 'text-emerald-400', POST: 'text-sky-400', PUT: 'text-amber-400',
  PATCH: 'text-violet-400', DELETE: 'text-rose-400',
};

const SURFACES = [
  ['insights', 'content'], ['kpi-monitor', 'content'], ['dashboard', 'view'],
  ['report', 'view'], ['explore', 'content'], ['activity', 'content'],
  ['investigate', 'content'], ['marketplace', 'content'], ['er-diagram', 'content'],
];

// Public, unauthenticated developer docs: embedding quickstart + read-only
// API reference rendered from the public OpenAPI spec. No login, no live keys.
export default function PublicDocsPage() {
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const baseBrand = useBrand();
  const [pubName, setPubName] = useState('');
  const [spec, setSpec] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  // This page is public (no auth), so there's no tenant context by default —
  // branding would fall back to whatever is cached or 'INZARA'. White-label
  // customers can link their devs to /developer-docs?brand=<their-login-slug>,
  // which resolves the brand name via the public branding endpoint.
  useEffect(() => {
    const slug = (new URLSearchParams(window.location.search).get('brand') || '').trim().toLowerCase();
    if (!slug) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/public/branding/${slug}`);
        if (!res.ok) return;
        const d = await res.json();
        if (alive && d?.app_name) setPubName(d.app_name);
      } catch { /* leave default branding */ }
    })();
    return () => { alive = false; };
  }, [apiBase]);

  const brand = useMemo(() => {
    const name = pubName || baseBrand.name || 'INZARA';
    const slug = (name.trim().split(/\s+/)[0] || 'inzara').toLowerCase().replace(/[^a-z0-9]/g, '') || 'inzara';
    return {
      name,
      slug,
      embedComponent: slug.charAt(0).toUpperCase() + slug.slice(1) + 'Embed',
      packageScope: `@${slug}`,
      hostToken: `YOUR_${slug.toUpperCase()}_HOST`,
      mgmtKeyEnv: `${slug.toUpperCase()}_MGMT_KEY`,
      hookPath: slug,
    };
  }, [pubName, baseBrand]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/mgmt/v1/openapi.json`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (alive) setSpec(data);
      } catch (e) {
        if (alive) setError(String(e?.message || e));
      }
    })();
    return () => { alive = false; };
  }, [apiBase]);

  const grouped = useMemo(() => {
    if (!spec?.paths) return [];
    const rows = [];
    for (const [path, methods] of Object.entries(spec.paths)) {
      for (const [method, op] of Object.entries(methods)) {
        if (!['get', 'post', 'patch', 'put', 'delete'].includes(method)) continue;
        rows.push({ path, method: method.toUpperCase(), summary: op.summary || '', group: classify(path) });
      }
    }
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? rows.filter(r => r.path.toLowerCase().includes(needle) || r.summary.toLowerCase().includes(needle) || r.method.toLowerCase().includes(needle))
      : rows;
    return GROUPS.map(g => ({ ...g, rows: filtered.filter(r => r.group === g.key) })).filter(g => g.rows.length);
  }, [spec, q]);

  return (
    <div style={{ minHeight: '100vh' }} className="bg-neutral-950 text-neutral-100" data-testid="public-docs-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">{brand.name} · Developer Docs</div>
        <h1 className="text-3xl font-semibold">API Reference &amp; Embedding Guide</h1>
        <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
          Public reference for embedding {brand.name} analytics and calling the Management API v1.
          The full machine-readable contract is at{' '}
          <a className="text-sky-400 hover:underline" href={`${apiBase}/api/mgmt/v1/openapi.json`} target="_blank" rel="noreferrer">openapi.json</a>.
        </p>

        {/* Embedding quickstart */}
        <section className="mt-8">
          <h2 className="text-lg font-semibold mb-2">Embedding quickstart</h2>
          <ol className="text-sm text-neutral-300 space-y-1.5 list-decimal list-inside">
            <li>On <strong>your server</strong>, mint a short-lived embed session with your management key — never ship the key to the browser.</li>
            <li>Pass the returned session token to the <code className="px-1 rounded bg-neutral-800">{brand.packageScope}/embed-react</code> or <code className="px-1 rounded bg-neutral-800">{brand.packageScope}/embed-js</code> SDK.</li>
            <li>Render one component/iframe for the surface you want.</li>
          </ol>
          <pre className="mt-3 bg-black/60 border border-neutral-800 rounded-lg p-3 text-[12px] overflow-x-auto"><code>{`// Server (Node) — mint a session
const r = await fetch('https://${brand.hostToken}/api/mgmt/v1/embed/sessions', {
  method: 'POST',
  headers: { Authorization: 'Bearer ' + process.env.${brand.mgmtKeyEnv}, 'Content-Type': 'application/json' },
  body: JSON.stringify({ workspace_id, sub: userId, resource: 'insights' }),
});
const { token } = await r.json();

// Client (React)
import { ${brand.embedComponent} } from '${brand.packageScope}/embed-react';
<${brand.embedComponent} host="https://${brand.hostToken}" token={token} resource="insights" />`}</code></pre>

          <h3 className="text-sm font-semibold mt-5 mb-2">Embeddable surfaces</h3>
          <div className="flex flex-wrap gap-2">
            {SURFACES.map(([v, kind]) => (
              <span key={v} className="text-xs px-2 py-1 rounded-full border border-neutral-700 bg-neutral-900" data-testid={`public-surface-${v}`}>
                <code>{v}</code> <span className="text-[10px] text-neutral-500">{kind}</span>
              </span>
            ))}
          </div>
          <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[13px] text-amber-200/90">
            <strong>Security:</strong> the management key mints sessions server-side only. Session tokens are short-lived and scoped to one tenant + workspace + user + resource. Admin panels require an elevated delegated admin-session token. Lock embedding to your allowed origins per workspace.
          </div>

          <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-[13px] text-emerald-200/90" data-testid="public-docs-origins-note">
            <strong>Allowed embed origins:</strong> lock which sites may mount a workspace&rsquo;s embeds. Set the allowlist with{' '}
            <code className="px-1 rounded bg-neutral-800 text-emerald-100">PUT /api/mgmt/v1/workspaces/&#123;workspace_id&#125;/embed-origins</code>{' '}
            passing <code className="px-1 rounded bg-neutral-800 text-emerald-100">{`{ "origins": ["https://app.acme.com"] }`}</code> (one <code>scheme://host[:port]</code> per entry, no path or wildcard). The allowlist is signed into every minted session token and enforced by the SDK against the embedding page&rsquo;s real origin. An <strong>empty list means the embed can be mounted from any site</strong>.
          </div>
        </section>

        {/* Partner guide: create reports & dashboards via API */}
        <section className="mt-10" data-testid="public-docs-content-authoring">
          <h2 className="text-lg font-semibold mb-3">Create reports &amp; dashboards via API</h2>
          <p className="text-[13px] text-neutral-400 mb-3">
            Partner apps can create and manage content in a workspace using a management API key. Grant the key the scopes
            <code className="mx-1 px-1 rounded bg-neutral-800 text-sky-200">reports:read reports:write dashboards:read dashboards:write datasets:read</code>
            and call these workspace-scoped endpoints (every write is audited; content appears in the workspace immediately):
          </p>
          <div className="rounded-lg border border-neutral-800 divide-y divide-neutral-800 mb-4">
            {[
              ['GET', '/api/mgmt/v1/workspaces/{workspace_id}/datasets', 'Discover semantic models + data sources to bind to'],
              ['GET', '/api/mgmt/v1/workspaces/{workspace_id}/reports', 'List reports'],
              ['POST', '/api/mgmt/v1/workspaces/{workspace_id}/reports', 'Create a report (Idempotency-Key supported)'],
              ['PUT', '/api/mgmt/v1/workspaces/{workspace_id}/reports/{report_id}', 'Update a report (partial)'],
              ['DELETE', '/api/mgmt/v1/workspaces/{workspace_id}/reports/{report_id}', 'Delete a report'],
              ['POST', '/api/mgmt/v1/workspaces/{workspace_id}/dashboards', 'Create a dashboard (Idempotency-Key supported)'],
              ['PUT', '/api/mgmt/v1/workspaces/{workspace_id}/dashboards/{dashboard_id}', 'Update a dashboard (partial)'],
            ].map(([m, p, s], i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2">
                <span className={`font-mono text-[11px] font-bold w-14 shrink-0 ${METHOD_COLOR[m] || ''}`}>{m}</span>
                <div className="min-w-0">
                  <div className="font-mono text-[12px] text-neutral-200 break-all">{p}</div>
                  <div className="text-[11px] text-neutral-500">{s}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[12px] text-neutral-500 mb-1">Example — create a report:</p>
          <pre className="text-[11px] bg-neutral-900 border border-neutral-800 rounded-lg p-3 overflow-x-auto text-neutral-300">{`curl -X POST \\
  https://your-host/api/mgmt/v1/workspaces/ws_123/reports \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Q3 Revenue","query":"…","visualization_type":"bar_chart","model_id":"mdl_…"}'`}</pre>
          <div className="mt-3 rounded-lg border border-sky-500/30 bg-sky-500/5 p-3 text-[13px] text-sky-200/90">
            <strong>Tip:</strong> pass an <code className="px-1 rounded bg-neutral-800 text-sky-100">Idempotency-Key</code> header on POSTs so a retry never creates a duplicate. To let end-users author inside your app, embed the dashboard/report with the <code className="px-1 rounded bg-neutral-800 text-sky-100">write</code> capability — the embedded builder can create new content at <code className="px-1 rounded bg-neutral-800 text-sky-100">/embed/v2/view/dashboard/new</code>.
          </div>
        </section>
        <section className="mt-10" data-testid="public-docs-webhooks">
          <h2 className="text-lg font-semibold mb-3">Get notified with webhooks</h2>
          <p className="text-[13px] text-neutral-400 mb-3">
            Rather than polling, subscribe to events (content created/updated/deleted, embed-origin
            changes, and more) and we&apos;ll POST a signed payload to your endpoint. Configure everything
            with a management key holding the
            <code className="mx-1 px-1 rounded bg-neutral-800 text-sky-200">webhooks:read webhooks:write</code>
            scopes:
          </p>
          <div className="rounded-lg border border-neutral-800 divide-y divide-neutral-800 mb-4">
            {[
              ['GET', '/api/mgmt/v1/webhooks/events', 'List every event type you can subscribe to'],
              ['GET', '/api/mgmt/v1/webhooks/endpoints', 'List your configured endpoints'],
              ['POST', '/api/mgmt/v1/webhooks/endpoints', 'Register an endpoint URL + event subscriptions'],
              ['POST', '/api/mgmt/v1/webhooks/endpoints/{endpoint_id}/rotate-secret', 'Rotate the signing secret'],
              ['POST', '/api/mgmt/v1/webhooks/endpoints/{endpoint_id}/test', 'Send a test delivery'],
              ['GET', '/api/mgmt/v1/webhooks/deliveries', 'Inspect recent delivery attempts'],
            ].map(([m, p, s], i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2">
                <span className={`font-mono text-[11px] font-bold w-14 shrink-0 ${METHOD_COLOR[m] || ''}`}>{m}</span>
                <div className="min-w-0">
                  <div className="font-mono text-[12px] text-neutral-200 break-all">{p}</div>
                  <div className="text-[11px] text-neutral-500">{s}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[12px] text-neutral-500 mb-1">Example — register an endpoint:</p>
          <pre className="text-[11px] bg-neutral-900 border border-neutral-800 rounded-lg p-3 overflow-x-auto text-neutral-300">{`curl -X POST \\
  https://your-host/api/mgmt/v1/webhooks/endpoints \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://your-app.com/hooks/${brand.hookPath}","events":["content.report.created","embed.origins.update"]}'`}</pre>
          <div className="mt-3 rounded-lg border border-sky-500/30 bg-sky-500/5 p-3 text-[13px] text-sky-200/90">
            <strong>Verify every delivery.</strong> Each POST carries an HMAC signature computed from your
            endpoint&apos;s signing secret — reject any request whose signature doesn&apos;t match. Rotate the secret
            anytime with the rotate-secret endpoint above.
          </div>
        </section>
        <section className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Management API v1</h2>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter endpoints…"
              className="text-xs px-3 py-1.5 rounded border border-neutral-700 bg-neutral-900 focus:outline-none focus:border-sky-500"
              data-testid="public-docs-filter"
            />
          </div>
          {error && <div className="text-sm text-rose-400" data-testid="public-docs-error">Could not load the API spec: {error}</div>}
          {!spec && !error && <div className="text-sm text-neutral-500">Loading reference…</div>}
          {grouped.map(g => (
            <div key={g.key} className="mb-5" data-testid={`public-group-${g.key}`}>
              <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">{g.label}</div>
              <div className="rounded-lg border border-neutral-800 divide-y divide-neutral-800">
                {g.rows.map((r, i) => (
                  <div key={`${r.method}-${r.path}-${i}`} className="flex items-start gap-3 px-3 py-2">
                    <span className={`font-mono text-[11px] font-bold w-14 shrink-0 ${METHOD_COLOR[r.method] || ''}`}>{r.method}</span>
                    <div className="min-w-0">
                      <div className="font-mono text-[12px] text-neutral-200 break-all">{r.path}</div>
                      {r.summary && <div className="text-[11px] text-neutral-500">{r.summary}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        <footer className="mt-10 pt-6 border-t border-neutral-800 text-[11px] text-neutral-600">
          {brand.name} Management API v1 · read-only public reference
        </footer>
      </div>
    </div>
  );
}
